/**
 * ListPickerModal.tsx — Dialog include/exclude multi-select for Maloum chat lists (Plan 08-05).
 *
 * Used twice in the BroadcastComposer Step 1 (targeting):
 *   - Include lists: "Send to" picker
 *   - Exclude lists: "Exclude" picker
 *
 * The `label` prop distinguishes the two instances ("Send to" vs "Exclude").
 *
 * Loads lists from GET /broadcasts/chat-lists (server returns friendlyName for
 * isManaged system lists — D-08-04a; no client-side re-mapping needed).
 *
 * Features:
 *   - Live search by friendlyName / name
 *   - Checkbox selection for each list row
 *   - Member count badge per row
 *   - Done (applies selection) / Cancel (discards)
 *
 * Semantic tokens only — no hardcoded hex/HSL.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, Button, Input, Badge } from '@crm/ui';
import { listsApi, type MaloumListDto } from '../../lib/api-client.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ListPickerModalProps {
  /** Creator account to load lists for */
  accountId: string;
  /** Currently selected list IDs (initialises the checked state) */
  selected: string[];
  /** Called with the confirmed selection when the user clicks Done */
  onConfirm: (ids: string[]) => void;
  /** Button label shown on the trigger (e.g. "Send to" or "Exclude") */
  label: string;
}

// ---------------------------------------------------------------------------
// ListPickerModal
// ---------------------------------------------------------------------------

export function ListPickerModal({
  accountId,
  selected,
  onConfirm,
  label,
}: ListPickerModalProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [checked, setChecked] = React.useState<Set<string>>(new Set(selected));

  // Sync checked state when the modal opens (in case `selected` changed externally)
  React.useEffect(() => {
    if (open) {
      setChecked(new Set(selected));
      setSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: lists = [], isLoading, isError } = useQuery({
    queryKey: ['chat-lists', accountId],
    queryFn: () => listsApi.getChatLists(accountId),
    enabled: open,
    staleTime: 60_000,
  });

  // Filter by search (case-insensitive, matches friendlyName or name)
  const filtered = React.useMemo(() => {
    if (!search.trim()) return lists;
    const needle = search.toLowerCase();
    return lists.filter(
      (l) =>
        l.friendlyName.toLowerCase().includes(needle) ||
        l.name.toLowerCase().includes(needle),
    );
  }, [lists, search]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleDone() {
    onConfirm([...checked]);
    setOpen(false);
  }

  function handleCancel() {
    setOpen(false);
  }

  // Render a human-readable summary of the current selection on the trigger button
  function selectionSummary(): string {
    if (selected.length === 0) return `${label}: none`;
    if (selected.length === 1) {
      // CR-02 FIX: server returns .id not ._id
      const found = lists.find((l) => l.id === selected[0]);
      return `${label}: ${found?.friendlyName ?? found?.name ?? selected[0]}`;
    }
    return `${label}: ${selected.length} lists`;
  }

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={[
          'flex items-center gap-2 px-3 py-2 text-sm rounded border border-border',
          'bg-bg-input text-text hover:border-accent/60 transition-colors text-left w-full',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg',
        ].join(' ')}
      >
        <span className="flex-1 truncate">{selectionSummary()}</span>
        <span className="text-text-muted text-xs">Edit</span>
      </button>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleCancel}
        title={label}
        className="max-w-lg"
      >
        <div className="flex flex-col gap-3" style={{ minHeight: '360px' }}>
          {/* Search input */}
          <Input
            placeholder="Search lists…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          {/* List */}
          <div className="flex-1 overflow-y-auto max-h-72 border border-border rounded divide-y divide-border">
            {isLoading && (
              <p className="text-sm text-text-muted p-4 text-center">Loading lists…</p>
            )}
            {isError && (
              <p className="text-sm text-danger p-4 text-center">
                Could not load lists. Check the account is active.
              </p>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <p className="text-sm text-text-muted p-4 text-center">
                {search ? 'No lists match your search.' : 'No lists found for this account.'}
              </p>
            )}
            {filtered.map((list) => (
              <ListRow
                key={list.id}
                list={list}
                checked={checked.has(list.id)}
                onToggle={() => toggle(list.id)}
              />
            ))}
          </div>

          {/* Selection summary */}
          {checked.size > 0 && (
            <p className="text-xs text-text-muted">
              {checked.size} list{checked.size !== 1 ? 's' : ''} selected
            </p>
          )}

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// ListRow
// ---------------------------------------------------------------------------

interface ListRowProps {
  list: MaloumListDto;
  checked: boolean;
  onToggle: () => void;
}

function ListRow({ list, checked, onToggle }: ListRowProps) {
  // CR-02 FIX: server returns .id not ._id
  const rowId = `list-row-${list.id}`;
  return (
    <label
      htmlFor={rowId}
      className={[
        'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors',
        checked ? 'bg-accent/10' : 'hover:bg-bg-hover',
      ].join(' ')}
    >
      <input
        id={rowId}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 accent-accent rounded"
      />
      <span className="flex-1 text-sm text-text truncate">
        {list.friendlyName || list.name}
      </span>
      {list.isManaged && (
        <Badge variant="info" className="text-xs shrink-0">
          System
        </Badge>
      )}
      <Badge variant="outline" className="text-xs font-mono shrink-0">
        {list.totalMemberCount.toLocaleString()}
      </Badge>
    </label>
  );
}
